import { PopupFormatSettings, PopupField, PopupRow, PopupStyle, PopupEvent } from 'features/builder/types'
import theme from 'features/common/ui/theme/cartoTheme'
import { createGlobalStyle } from 'styled-components'
import { alpha } from '@mui/material/styles'
import clsx from 'clsx'
import DOMPurify from 'dompurify'
import { useAppSelector } from 'app/state'
import { ProviderType } from 'features/connections'
import { getColumnAliasForAggregationExp } from 'features/builder/state/spatialIndexUtils'

export const popupClasses = {
  popup: 'CDB-Popup',
  popupDark: 'CDB-Popup--dark',
  popupHighlightFirst: 'CDB-Popup--highlightFirst',
  frozen: 'CDB-Frozen',
  pointerBottomLeft: 'CDB-PointerBottomLeft',
  pointerBottomRight: 'CDB-PointerBottomRight',
  pointerTopLeft: 'CDB-PointerTopLeft',
  pointerTopRight: 'CDB-PointerTopRight',
  panel: 'CDB-SidePanelContent'
}

const typographyCaptionStyle = `
  font-size: 0.688rem;
  font-family: Inter, sans-serif;
  font-weight: 500;
  line-height: 1em;
  letter-spacing: 0.0125em;
`

const selectableStyle = `
  user-select: text;
  -moz-user-select: text;
  -webkit-user-select: text;
`

export const GlobalPopupStyle = createGlobalStyle`
  .${popupClasses.popup} {
    --bgColor: ${theme.palette.common.white};
    min-width: 120px;
    max-width: 240px;
    background-color: var(--bgColor);
    border-radius: ${theme.shape.borderRadius}px;
    box-shadow: ${theme.shadows[2]};
    padding: ${theme.spacing(1.5, 1.5, 0.5, 1.5)};

    dl {
      margin: 0;
    }
    dl>div {
      margin-bottom: ${theme.spacing(1)};
    }
    dt {
      ${typographyCaptionStyle}
      ${selectableStyle}
      color: ${theme.palette.text.secondary};
    }
    dd {
      ${typographyCaptionStyle}
      ${selectableStyle}
      margin: 0;
      line-height: 1.45;
    }
  }

  .${popupClasses.frozen} {
    .${popupClasses.popup} {
      min-width: 200px;
      max-width: 320px;
      max-height: 320px;
      overflow-y: scroll;
      padding: ${theme.spacing(2, 4, 1.5, 2)};

      dd {
        font-weight: normal;
        font-size: 13px;
        line-height: 1.54;
        letter-spacing: 0.25px;
      }

      &::after {
        content: '';
        position: absolute;
        border-style: solid;
        width: 0;
        height: 0;
      }
    }
    &.${popupClasses.pointerTopLeft} .${popupClasses.popup} {
      border-top-left-radius: 0;
      &::after {
        left: 0;
        top: ${theme.spacing(-2)};
        border-width: 0 ${theme.spacing(2)} ${theme.spacing(2)} 0;
        border-color: transparent transparent var(--bgColor) transparent;
        filter: drop-shadow(0 -2px 1px ${alpha(theme.palette.common.black, 0.16)});
      }
    }
    &.${popupClasses.pointerBottomLeft} .${popupClasses.popup} {
      border-bottom-left-radius: 0;
      &::after {
        left: 0;
        bottom: ${theme.spacing(-2)};
        border-width: ${theme.spacing(2)} ${theme.spacing(2)} 0 0;
        border-color: var(--bgColor) transparent transparent transparent;
        filter: drop-shadow(0 2px 1px ${alpha(theme.palette.common.black, 0.16)});
      }
    }
    &.${popupClasses.pointerBottomRight} .${popupClasses.popup} {
      border-bottom-right-radius: 0;
      &::after {
        right: 0;
        bottom: ${theme.spacing(-2)};
        border-width: 0 ${theme.spacing(2)} ${theme.spacing(2)} 0;
        border-color: transparent var(--bgColor) transparent transparent;
        filter: drop-shadow(0 2px 1px ${alpha(theme.palette.common.black, 0.16)});
      }
    }
    &.${popupClasses.pointerTopRight} .${popupClasses.popup} {
      border-top-right-radius: 0;
      &::after {
        right: 0;
        top: ${theme.spacing(-2)};
        border-width: ${theme.spacing(2)} ${theme.spacing(2)} 0 0;
        border-color: transparent var(--bgColor) transparent transparent;
        filter: drop-shadow(0 -2px 1px ${alpha(theme.palette.common.black, 0.16)});
      }
    }
  }

  .${popupClasses.popupDark} {
    --bgColor: ${theme.palette.common.black};
    &::-webkit-scrollbar-thumb {
      background-color: ${alpha(theme.palette.common.white, 0.12)};
    }
    dt {
      color: ${alpha(theme.palette.common.white, 0.6)};
    }
    dd {
      color: ${theme.palette.common.white};
    }
  }

  .${popupClasses.popupHighlightFirst} dl>div:first-of-type dd {
    font-family: Inter, sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.54;
    letter-spacing: 0.25px;
  }
  .${popupClasses.frozen}.${popupClasses.popupHighlightFirst} dl>div:first-of-type dd {
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.5;
    letter-spacing: 0.15px;
  }

  .${popupClasses.panel} {
    dl>div {
      padding-bottom: ${theme.spacing(1.5)};
      margin-bottom: ${theme.spacing(1.5)};
      border-bottom: 1px solid ${theme.palette.divider}
    }
    dt {
      ${typographyCaptionStyle};
      color: ${theme.palette.text.secondary};
      line-height: 20px;
    }
    dd {
      margin: 0;
      font-family: Inter, sans-serif;
      font-weight: normal;
      font-size: 13px;
      line-height: 1.54;
      letter-spacing: 0.25px;
      color: ${theme.palette.text.primary}
    }
  }
`

const fieldToTemplate = (field: PopupField, provider: ProviderType): string => {
  const fieldName = field.spatialIndexAggregation
    ? getColumnAliasForAggregationExp(field.name, field.spatialIndexAggregation, provider)
    : field.name

  return `
    <div>
      <dt>${field.customName || fieldName}</dt>
      <dd>{{${fieldName}}}</dd>
    </div>`
}

export const fieldListToTemplate = ({ fields, style }: PopupFormatSettings, provider: ProviderType): string => {
  if (style === PopupStyle.None) {
    return ''
  }
  const list =
    fields.length > 0
      ? `
  <dl>${fields.map((field) => fieldToTemplate(field, provider)).join('')}
  </dl>`
      : ''
  return `<div class="${clsx(
    style === PopupStyle.Panel ? popupClasses.panel : popupClasses.popup,
    (style === PopupStyle.Dark || style === PopupStyle.DarkWithHiFirst) && popupClasses.popupDark,
    (style === PopupStyle.LightWithHiFirst || style === PopupStyle.DarkWithHiFirst) && popupClasses.popupHighlightFirst
  )}">${list}
</div>`
}

const expressionRegexp = /[()+*/%&|?:.-]/
const placeholderRegexp = /{{\s*[\w\s()+*/%&|?:.-]+\s*}}/g
const placeholderToFieldName = (placeHolder: string) => placeHolder.replace(/{|}|\s/g, '')

export const renderTemplate = (template = '', rows: PopupRow[]): string => {
  // HTML templates are sanitized on save for user visibility, and sanitized
  // again before rendering (avoid trust that stored HTML is safe from XSS).
  return sanitizeTemplate(template).replaceAll(placeholderRegexp, (placeholder) => {
    const fieldName = placeholderToFieldName(placeholder)
    const value = rows.find((row) => row.field.name === fieldName)?.value.displayValue || '-'
    return value
  })
}

function isExpression(value: string): boolean {
  return value.match(expressionRegexp) !== null
}
export const getFieldsFromTemplate = (template = ''): PopupField[] => {
  let names = (template.match(placeholderRegexp) || []).map(placeholderToFieldName)
  names = Array.from(new Set(names)) // remove duplicates
  return names.map((name) => ({ name, isExpression: isExpression(name) }))
}

export const sanitizeTemplate = (template = ''): string => {
  return DOMPurify.sanitize(template, { ADD_ATTR: ['target'] })
}

export const usePopupTemplate = (layerId = '', event: PopupEvent): string | undefined => {
  const popupSettings = useAppSelector((state) => state.builder?.popupSettings || { layers: {} })
  if (!layerId) {
    return undefined
  }
  const layerPopupSettings = layerId && popupSettings.layers[layerId]
  const eventSettings = layerPopupSettings?.[event]
  return eventSettings?.templateMode ? eventSettings.template : undefined
}
